import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, Loader2, ShoppingBag, MessageCircle } from "lucide-react";
import { API, LOGO_URL } from "@/lib/api";
import { useLead } from "@/context/LeadDialog";

const SESSION_KEY = "asure_chat_session";
const WA_NUMBER = "918269784515";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) { sid = "sess_" + Math.random().toString(36).slice(2, 12) + Date.now(); localStorage.setItem(SESSION_KEY, sid); }
  return sid;
}

const SUGGESTIONS = [
  "What's the difference between Spirulina and Sea Buckthorn?",
  "How do I take the 30-day box?",
  "Do you deliver to my city?",
  "I'm interested — please contact me",
];

const QUICK_BUY = [
  { label: "Combo (₹899)", value: "Spirulina + Sea Buckthorn — 30 Day Box (Combo)" },
  { label: "Spirulina", value: "Spirulina" },
  { label: "Sea Buckthorn", value: "Sea Buckthorn" },
  { label: "Noni", value: "Noni" },
  { label: "Flax Oil", value: "Flax Oil" },
  { label: "Glucosamine", value: "Glucosamine" },
];

export default function ChatWidget() {
  const loc = useLocation();
  const { openLead } = useLead();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi — I'm the ASURE Wellness Assistant. Ask me about our products, ingredients, delivery or anything else." },
  ]);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, busy]);

  if (loc.pathname.startsWith("/admin") || loc.pathname === "/login") return null;

  const escalateToWhatsApp = () => {
    // Compose a transcript from the last ~8 turns (skip greeting)
    const recent = msgs.slice(1).slice(-8);
    const transcript = recent.map((m) => `${m.role === "user" ? "Me" : "ASURE Assistant"}: ${m.content.replace(/\[LEAD_INTENT\]/g, "").trim()}`).join("\n\n");
    const preface = "Hi ASURE, I was chatting with the AI assistant and would like to talk to a human expert.\n\n---";
    const text = transcript ? `${preface}\n${transcript}` : `${preface}\n(no conversation yet)`;
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: q }, { id: `a-${Date.now()}`, role: "assistant", content: "", streaming: true }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: q }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = ""; let leadIntent = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "");
          if (!line) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMsgs((m) => {
                const next = [...m]; const last = next[next.length - 1];
                if (last?.streaming) next[next.length - 1] = { ...last, content: last.content + j.delta };
                return next;
              });
            } else if (j.done) {
              leadIntent = !!j.lead_intent;
              setMsgs((m) => {
                const next = [...m]; const last = next[next.length - 1];
                if (last) next[next.length - 1] = { ...last, streaming: false };
                return next;
              });
            } else if (j.error) {
              setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: "Sorry, I couldn't reach the assistant. Please try again in a moment." }]);
            }
          } catch (e) { console.warn("chat sse parse", e); }
        }
      }
      if (leadIntent) setTimeout(() => openLead("General Enquiry"), 400);
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: "Sorry, I couldn't reach the assistant. Please try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* FAB — anchored above the WhatsApp button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open ASURE Assistant"
        data-testid="chat-fab"
        className="fixed z-40 right-4 md:right-6 bottom-[140px] lg:bottom-[76px] rounded-full shadow-lg hover:shadow-xl transition-shadow"
        style={{ background: "linear-gradient(135deg, var(--asure-forest), var(--asure-deep))" }}
      >
        <span className="flex items-center gap-2 pl-3 pr-4 py-3 text-white text-sm font-semibold">
          <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.25)", color: "var(--asure-gold)" }}><Sparkles size={14} /></span>
          <span className="hidden md:inline">Ask ASURE</span>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:justify-end bg-black/40 p-0 md:p-6" onClick={() => setOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full md:w-[420px] h-[85vh] md:h-[600px] bg-white rounded-t-3xl md:rounded-3xl overflow-hidden flex flex-col shadow-2xl border border-[color:var(--asure-border)]"
            data-testid="chat-panel"
          >
            <div className="p-4 flex items-center gap-3 text-white" style={{ background: "linear-gradient(135deg, var(--asure-forest), var(--asure-deep))" }}>
              <img src={LOGO_URL} alt="" className="h-9 w-9 rounded-lg bg-white p-1" />
              <div className="flex-1">
                <div className="font-display text-lg leading-none">ASURE Assistant</div>
                <div className="text-[11px] text-white/70 mt-1">Powered by AI · not medical advice</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/80 hover:text-white" data-testid="chat-close"><X size={18} /></button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-asure-ivory">
              {msgs.map((m, i) => (
                <div key={m.id || `${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-asure-forest text-white rounded-br-sm" : "bg-white border border-[color:var(--asure-border)] text-asure-text rounded-bl-sm"}`}>
                    {m.content || (m.streaming ? <span className="inline-flex items-center gap-2 text-asure-text2"><Loader2 size={12} className="animate-spin" /> thinking…</span> : null)}
                  </div>
                </div>
              ))}
            </div>

            {msgs.length <= 1 && (
              <div className="px-4 pb-2 pt-1 grid grid-cols-1 gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-left text-xs bg-white border border-[color:var(--asure-border)] rounded-full px-3 py-2 hover:border-asure-forest transition-colors" data-testid={`chat-suggestion-${s.slice(0, 10)}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[color:var(--asure-border)] bg-white">
              {/* Quick-Buy chips — always visible */}
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
                {QUICK_BUY.map((q) => (
                  <button
                    type="button"
                    key={q.value}
                    onClick={() => openLead(q.value)}
                    data-testid={`chat-quickbuy-${q.value.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                    className="whitespace-nowrap inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-asure-forest text-white hover:bg-asure-deep transition-colors"
                    title="Buy this — send me a callback"
                  >
                    <ShoppingBag size={11} />{q.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={escalateToWhatsApp}
                  data-testid="chat-escalate-whatsapp"
                  className="whitespace-nowrap inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-[#25D366] text-white hover:brightness-95 transition-all"
                  title="Talk to an ASURE expert on WhatsApp"
                >
                  <MessageCircle size={11} />Talk to Expert
                </button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about ASURE products…"
                  data-testid="chat-input"
                  disabled={busy}
                  className="flex-1 border border-[color:var(--asure-border)] rounded-full px-4 py-2.5 text-sm outline-none focus:border-asure-forest disabled:opacity-60"
                />
                <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send"
                  className="w-10 h-10 rounded-full bg-asure-orange text-white inline-flex items-center justify-center disabled:opacity-50 hover:bg-[#e06d13]">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
